/**
 * Centralized File Validation Utility
 * Consolidates file validation logic used across:
 * - ChatInput.tsx (paperclip attachments)
 * - DocumentUploadZone.tsx (drag-and-drop)
 * - ChatContext.tsx (addFile function)
 */

/** Valid file extensions for upload */
export const VALID_FILE_EXTENSIONS = [
  '.txt', '.md', '.xml', '.cda', '.pdf', '.csv',
  '.json', '.hl7',
  '.jpg', '.jpeg', '.png', '.webp', '.gif'
] as const

/** MIME types accepted by file input elements */
const VALID_MIME_TYPES = [
  'text/plain',
  'text/markdown',
  'application/xml',
  'text/xml',
  'application/pdf',
  'text/csv',
  'application/json',
  'application/x-hl7',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif'
] as const

/** Maximum file size in bytes (10MB) */
const MAX_FILE_SIZE = 10 * 1024 * 1024

/** Maximum file size in human-readable format */
const MAX_FILE_SIZE_DISPLAY = '10MB'

interface FileValidationResult {
  valid: boolean
  error?: string
}

/**
 * Validate a file for upload
 * @param file - File to validate
 * @returns Validation result with error message if invalid
 */
export function validateFile(file: File): FileValidationResult {
  // Check file extension
  const ext = '.' + file.name.toLowerCase().split('.').pop()
  if (!VALID_FILE_EXTENSIONS.includes(ext as typeof VALID_FILE_EXTENSIONS[number])) {
    return {
      valid: false,
      error: `Invalid file type "${ext}". Accepted: ${VALID_FILE_EXTENSIONS.join(', ')}`
    }
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File too large. Maximum size: ${MAX_FILE_SIZE_DISPLAY}`
    }
  }

  return { valid: true }
}

/**
 * Get the accept attribute value for file input elements
 * Includes both extensions and MIME types for better browser compatibility
 */
export function getFileInputAccept(): string {
  return [
    ...VALID_FILE_EXTENSIONS,
    ...VALID_MIME_TYPES
  ].join(',')
}

